import { useRef } from 'react'
import { motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion'

/**
 * Wraps a child in a spring-followed pointer offset. The magnetism is a
 * decorative enhancement only, so it is skipped entirely when the visitor asks
 * for reduced motion (DESIGN.md §6) and when a coarse pointer is in use.
 */
export default function MagneticButton({ children, strength = 0.35, className, ...props }) {
  const ref = useRef(null)
  const reduceMotion = useReducedMotion()
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const sx = useSpring(x, { stiffness: 200, damping: 15, mass: 0.2, restDelta: 0.01 })
  const sy = useSpring(y, { stiffness: 200, damping: 15, mass: 0.2, restDelta: 0.01 })

  const enabled = !reduceMotion

  const handleMove = (e) => {
    if (!enabled || !ref.current) return
    const rect = ref.current.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    x.set((e.clientX - rect.left - rect.width / 2) * strength)
    y.set((e.clientY - rect.top - rect.height / 2) * strength)
  }

  const reset = () => {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.span
      ref={ref}
      className={className}
      style={{ x: sx, y: sy, display: 'inline-block' }}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      onBlur={reset}
      {...props}
    >
      {children}
    </motion.span>
  )
}
